import React, { useEffect, useRef } from 'react';
import { pickHomeHeroAuraStep } from '../../utils/auraColor';

/** Matches the hero cloud's color cadence so the aura flips in the same rhythm. */
const AURA_CYCLE_MS = 1500;

const AURA_CSS_VAR_KEYS = [
  '--aura-rgb-light',
  '--aura-rgb',
  '--aura-rgb-dark',
  '--aura-rgb-darker',
  '--aura-rgb-darkest',
  '--aura-digit-highlight',
  '--aura-glow-soft',
  '--aura-glow-mid',
  '--aura-glow-strong',
  '--aura-glow-digit',
  '--aura-icon-glow-1',
  '--aura-icon-glow-2',
];

function applyAuraStyleVars(element, vars) {
  if (!element) return;
  if (!vars) {
    AURA_CSS_VAR_KEYS.forEach((key) => element.style.removeProperty(key));
    return;
  }
  Object.entries(vars).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });
}

/**
 * A 3D Valence / Arousal / Dominance coordinate space that houses a morphing
 * "vibe aura" cloud (same look + color cadence as the hero cloud). The VAD
 * dimensions are how the visual-vibe transformer places a vibe in emotion
 * space. Visually decorative; pass `onClick` to make it a button (e.g. open
 * the fox chat).
 */
export default function VibeAuraVadSpace({ onClick = null }) {
  const auraRef = useRef(null);
  const auraRef2 = useRef(null);

  useEffect(() => {
    const els = [auraRef.current, auraRef2.current].filter(Boolean);
    if (els.length === 0) return undefined;

    const prevKeys = new WeakMap();
    const applyStep = () => {
      els.forEach((el) => {
        const step = pickHomeHeroAuraStep(prevKeys.get(el) || null);
        prevKeys.set(el, step.key);
        applyAuraStyleVars(el, step.vars);
      });
    };

    applyStep();

    if (typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const id = window.setInterval(applyStep, AURA_CYCLE_MS);
    return () => window.clearInterval(id);
  }, []);

  const scene = (
      <div className="vad-space__scene" aria-hidden="true">
        <span className="vad-grid vad-grid--floor" />
        <span className="vad-grid vad-grid--back" />
        <span className="vad-grid vad-grid--side" />
        <span className="vad-axis vad-axis--valence" />
        <span className="vad-axis vad-axis--arousal" />
        <span className="vad-axis vad-axis--dominance" />
        <span className="vad-origin" />

        <span className="vad-aura" ref={auraRef}>
          <span className="vad-aura__blob vad-aura__blob--one" />
          <span className="vad-aura__blob vad-aura__blob--two" />
          <span className="vad-aura__blob vad-aura__blob--three" />
        </span>

        <span className="vad-aura vad-aura--two" ref={auraRef2}>
          <span className="vad-aura__blob vad-aura__blob--one" />
          <span className="vad-aura__blob vad-aura__blob--two" />
          <span className="vad-aura__blob vad-aura__blob--three" />
        </span>

        <span className="vad-label vad-label--valence">
          <span className="vad-label__accent">D</span>
        </span>
        <span className="vad-label vad-label--arousal">
          <span className="vad-label__accent">A</span>
        </span>
        <span className="vad-label vad-label--dominance">
          <span className="vad-label__accent">V</span>
        </span>
      </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className="vad-space vad-space--clickable"
        onClick={onClick}
        aria-label="Set up your visual-vibe transformer"
      >
        {scene}
      </button>
    );
  }

  return (
    <div className="vad-space" aria-hidden="true">
      {scene}
    </div>
  );
}
